<template>
  <div class="relative">
    <!-- Input de búsqueda -->
    <div 
      class="w-full px-3 py-2 border border-n-weak rounded-md bg-white dark:bg-n-solid-1 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 cursor-pointer"
      @click="toggleDropdown"
    >
      <div v-if="selectedContact" class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-sm font-medium text-blue-700 dark:text-blue-300">
            {{ getContactInitials(selectedContact) }}
          </div>
          <div class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">{{ selectedContact.name }}</span>
            <span class="text-xs text-n-slate-11">{{ selectedContact.email }}</span>
          </div>
        </div>
        <button 
          @click.stop="clearSelection"
          class="text-n-slate-11 hover:text-n-slate-12 text-lg"
          type="button"
        >
          ×
        </button>
      </div>
      <div v-else class="flex items-center justify-between">
        <span class="text-n-slate-11">{{ placeholder }}</span>
        <svg class="w-4 h-4 text-n-slate-11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>

    <!-- Dropdown -->
    <div 
      v-if="showDropdown" 
      class="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-n-solid-1 border border-n-weak rounded-md shadow-lg z-50 max-h-80 overflow-hidden"
    >
      <!-- Input de búsqueda dentro del dropdown -->
      <div class="p-3 border-b border-n-weak">
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          placeholder="Buscar contacto por nombre, email o teléfono..."
          class="w-full px-3 py-2 border border-n-weak rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-n-solid-2"
          @input="handleSearch"
          @keydown.escape="closeDropdown"
        />
      </div>

      <!-- Lista de contactos -->
      <div 
        ref="contactsList"
        class="max-h-60 overflow-y-auto"
        @scroll="handleScroll"
      >
        <div v-if="isLoading && contacts.length === 0" class="p-4 text-center text-n-slate-11">
          <div class="animate-spin inline-block w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          <span class="ml-2">Cargando contactos...</span>
        </div>

        <div v-else-if="contacts.length === 0 && !isLoading" class="p-4 text-center text-n-slate-11">
          {{ searchQuery ? 'No se encontraron contactos' : 'No hay contactos disponibles' }}
        </div>

        <div v-else>
          <div
            v-for="contact in contacts"
            :key="contact.id"
            @click="selectContact(contact)"
            class="flex items-center gap-3 p-3 hover:bg-n-slate-2 dark:hover:bg-n-solid-3 cursor-pointer border-b border-n-weak last:border-b-0"
          >
            <div class="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-sm font-medium text-blue-700 dark:text-blue-300">
              {{ getContactInitials(contact) }}
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-n-slate-12 truncate">
                {{ contact.name || 'Sin nombre' }}
              </div>
              <div class="text-xs text-n-slate-11 truncate">
                {{ contact.email || contact.phone_number || 'Sin información de contacto' }}
              </div>
            </div>
          </div>

          <!-- Indicador de carga para paginación -->
          <div v-if="isLoading && contacts.length > 0" class="p-3 text-center text-n-slate-11">
            <div class="animate-spin inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
            <span class="ml-2 text-xs">Cargando más contactos...</span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Overlay para cerrar el dropdown -->
  <div
    v-if="showDropdown"
    class="fixed inset-0 z-40"
    @click="closeDropdown"
  ></div>
</template>

<script>
import ContactAPI from 'dashboard/api/contacts.js';

export default {
  name: 'SearchableContactSelect',
  
  props: {
    modelValue: {
      type: [Number, null],
      default: null
    },
    placeholder: {
      type: String,
      default: 'Seleccionar contacto'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  emits: ['update:modelValue'],

  data() {
    return {
      showDropdown: false,
      searchQuery: '',
      contacts: [],
      isLoading: false,
      currentPage: 1,
      hasMorePages: true,
      searchTimeout: null,
      selectedContact: null,
      totalContacts: 0
    };
  },

  computed: {
    contactValue: {
      get() {
        return this.modelValue;
      },
      set(newValue) {
        this.$emit('update:modelValue', newValue);
      }
    }
  },

  watch: {
    modelValue: {
      handler(newValue) {
        if (newValue && !this.selectedContact) {
          this.loadSelectedContact(newValue);
        } else if (!newValue) {
          this.selectedContact = null;
        }
      },
      immediate: true
    }
  },

  methods: {
    async toggleDropdown() {
      if (this.disabled) return;
      
      if (this.showDropdown) {
        this.closeDropdown();
      } else {
        this.showDropdown = true;
        this.resetSearch();
        await this.loadContacts(true);
        
        // Focus en el input de búsqueda
        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus();
          }
        });
      }
    },

    closeDropdown() {
      this.showDropdown = false;
      this.searchQuery = '';
    },

    resetSearch() {
      this.searchQuery = '';
      this.currentPage = 1;
      this.hasMorePages = true;
      this.contacts = [];
    },

    async loadContacts(reset = false) {
      if (this.isLoading) return;
      
      if (reset) {
        this.currentPage = 1;
        this.hasMorePages = true;
        this.contacts = [];
      }

      if (!this.hasMorePages) return;

      this.isLoading = true;

      try {
        let response;
        
        if (this.searchQuery.trim()) {
          // Búsqueda con query
          response = await ContactAPI.search(this.searchQuery.trim(), this.currentPage, 'name');
        } else {
          // Carga normal paginada
          response = await ContactAPI.get(this.currentPage, 'name');
        }

        const newContacts = response.data.payload || [];
        this.totalContacts = response.data.meta?.count || 0;

        if (reset) {
          this.contacts = newContacts;
        } else {
          this.contacts = [...this.contacts, ...newContacts];
        }

        // Determinar si hay más páginas
        const totalPages = Math.ceil(this.totalContacts / 15); // RESULTS_PER_PAGE = 15
        this.hasMorePages = this.currentPage < totalPages;
        
        if (newContacts.length > 0) {
          this.currentPage += 1;
        } else {
          this.hasMorePages = false;
        }

      } catch (error) {
        console.error('Error loading contacts:', error);
        this.hasMorePages = false;
      } finally {
        this.isLoading = false;
      }
    },

    async loadSelectedContact(contactId) {
      if (!contactId) return;
      
      // Primero buscar en los contactos ya cargados
      const existingContact = this.contacts.find(c => c.id === contactId);
      if (existingContact) {
        this.selectedContact = existingContact;
        return;
      }

      // Si no está en la lista, hacer una consulta específica
      try {
        const response = await ContactAPI.show(contactId);
        this.selectedContact = response.data.payload;
      } catch (error) {
        console.error('Error loading selected contact:', error);
        this.selectedContact = null;
      }
    },

    handleSearch() {
      // Limpiar timeout anterior
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Debounce de 300ms
      this.searchTimeout = setTimeout(async () => {
        await this.loadContacts(true);
      }, 300);
    },

    handleScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      
      // Cargar más cuando esté cerca del final (100px antes del final)
      if (scrollHeight - scrollTop - clientHeight < 100 && this.hasMorePages && !this.isLoading) {
        this.loadContacts(false);
      }
    },

    selectContact(contact) {
      this.selectedContact = contact;
      this.contactValue = contact.id;
      this.closeDropdown();
    },

    clearSelection() {
      this.selectedContact = null;
      this.contactValue = null;
    },

    getContactInitials(contact) {
      if (!contact || !contact.name) return '?';
      
      const names = contact.name.split(' ').filter(n => n.length > 0);
      if (names.length === 1) {
        return names[0].charAt(0).toUpperCase();
      } else {
        return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
      }
    }
  },

  beforeUnmount() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }
};
</script>

<style scoped>
/* Estilos específicos si son necesarios */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>